import { Box } from '@mui/material'
import { FS, V2, focusRing, hoverOnly } from '../../theme/tokens'
import { v2Tone } from '../../theme/theme'

/** Tones that resolve through `theme.palette.v2`. `plain` is the neutral wash. */
const TONES = ['plain', 'ok', 'warn', 'err', 'info', 'agent', 'brand']

/**
 * Pill — the one chip shape for status, semantics, filters and moves.
 *
 * @description
 *   Design-Language v2 makes everything chip-shaped: state, semantics, filter
 *   facets, agent moves (Muster 4). This is that shape, derived from
 *   `SitChip`/`toneSx` (rma/components/SituationHeader.jsx, deleted 2026-08-11) so the
 *   overview and the case detail end up with ONE chip language.
 *   ✅ They HAVE it, since 2026-08-11. `SitChip`, `toneSx` and `SIT_CHIP_SX`
 *   are deleted and the case detail's Lage row is built from this component —
 *   court chip, every SLA chip, and the interactive follow-up chip, migrated
 *   in ONE commit. That was not tidiness: a mutation run put a single SLA chip
 *   back on the old box and the test written for exactly that case stayed
 *   GREEN, because the default fixture carries `active_slas: []` and never
 *   renders the branch. Migrate a chip row whole, and prove it with a fixture
 *   that shows every chip KIND at once — two chip languages in one row remain
 *   worse than either end state, and they are easy to ship undetected.
 *
 *   🔴 The default border is `1px solid transparent`, and that is structural,
 *   not decoration. The deleted `SIT_CHIP_SX` documented why: `FollowUpChip` draws
 *   a real dashed border, and without the reserved box its neighbours sit 2px
 *   shorter and nudge the row. The canon's "soft fill + FULL border" rule
 *   (Muster 5) governs state SURFACES — alarm and dead-end ROWS — not every
 *   chip inside them. Where a pill itself carries state, opt in with
 *   `bordered`, which draws the full -100 tone.
 *
 *   Tone resolves through `theme.palette.v2`, so no caller passes `isDark`.
 *   That is the whole reason the dark siblings for ok/warn/info were added:
 *   before them, a shared pill could only have rendered dark-mode tones by
 *   carrying hex literals into `components/common/`, which the SOT forbids.
 *
 * @useWhen
 *   - Situation / status chips in a case row or header
 *   - Filter facets with a count (`count`, tabular figures)
 *   - An agent move with its autonomy tier (`tier`)
 *   - Any chip needing a small trailing qualifier (`suffix`)
 *
 * @dontUseFor
 *   - Mobile glass filter rows — that is FilterPillRow / GLASS_PILL_SX (T2)
 *   - The move strip in the case detail — that is MovePillStrip
 *   - Primary actions — a CTA is a Button, not a chip
 *
 * @related
 *   - TierBadge — the STANDALONE tier badge; `tier` here renders the canon's
 *     other pattern, a text suffix, and the two must not be merged
 *   - ProvenanceMark — provenance inline, outside a chip row
 *   - FilterPillRow — the mobile glass pill, a different tier of the system
 *
 * @reuseHints
 *   - PO status chips could drop their bespoke styling onto this
 *   - Scraper health states in the target list
 *
 * @props
 *   children                          the label
 *   tone?: 'plain'|'ok'|'warn'|'err'|'info'|'agent'|'brand'   default 'plain'
 *                                     `brand` is the canon's `primary` row
 *                                     (design-language-proposal.md:217), live
 *                                     in BOTH modes. It was struck from the P1
 *                                     plan as "a tone without tokens"; the
 *                                     tokens were what was missing, and they
 *                                     landed with this change.
 *   suffix?: string                   free trailing qualifier in the tier style.
 *                                     Open on purpose: the canon uses the same
 *                                     style for non-tier suffixes such as
 *                                     "Surface 4" on a diagnosis pill
 *                                     (inbox-triage.html:385). A closed tier
 *                                     enum would have forced consumers to fork it.
 *   tier?: 'T0'|'T1'|'T2'|'T3'        typed convenience wrapper over `suffix`
 *   icon?: ReactNode                  leading icon
 *   count?: number                    facet count, tabular figures.
 *                                     Rendered on FULL ink (`V2.ink` /
 *                                     `V2.inkDark`) at `opacity: 0.7`, not on
 *                                     the root's `ink70` — double-damping it
 *                                     landed at ~ink49 / 3.15–3.69:1, below
 *                                     WCAG AA for 12.5 px text (R16-B1,
 *                                     2026-11-03). Load-bearing info never
 *                                     below ink70 (tokens.js:1716-1717).
 *   selected?: boolean                filter state. LEAVE UNDEFINED for a pill
 *                                     that is not a toggle: `aria-pressed`
 *                                     follows `selected !== undefined`, not
 *                                     `onClick`. The same pill serves as a
 *                                     filter facet (a real toggle) AND as a
 *                                     recommendation chip that opens the case
 *                                     detail — announcing the latter as a
 *                                     toggle promises a state change that
 *                                     never comes.
 *                                     Since 2026-08-09 (Owner-Entscheid 5) the
 *                                     BORDER carries the state at 3:1+ against
 *                                     the pill's own fill — `ink70` for plain
 *                                     (6.00 light / 4.81 dark), the tone's
 *                                     `main` otherwise (4.75 / 4.59 worst tone).
 *                                     `plain` additionally steps its fill to
 *                                     `V2.washSelected` (1.13 / 1.27) as the
 *                                     second cue. Toned pills do NOT step their
 *                                     fill — see the comment at the style block
 *                                     for the AA measurement that forbids it.
 *   nowrap?: boolean                  default FALSE. Situation labels are
 *                                     sentences ("Sackgasse: Guards
 *                                     blockieren") and must wrap rather than
 *                                     overflow; the props contract asks for
 *                                     "kein Truncate by default". Opt in where
 *                                     a short chip must stay on one line.
 *   bordered?: boolean                draw the full -100 border (state-carrying pills)
 *   disabled?: boolean                a pill that is present but not available.
 *                                     Added 2026-08-05 (Owner-Entscheid E2).
 *                                     🔴 It used to ride in `...rest`, which
 *                                     meant it behaved THREE different ways in
 *                                     one component: on the `<button>` branch it
 *                                     landed as the native attribute and worked;
 *                                     on the `<span>` branch it became
 *                                     `disabled="true"` on a span, which is
 *                                     invalid HTML and does nothing; and in
 *                                     neither branch was there any disabled
 *                                     LOOK, so a pill the user cannot use was
 *                                     indistinguishable from one they can.
 *                                     Now it is one prop with one meaning:
 *                                     `aria-disabled` on both branches (a span
 *                                     cannot carry `disabled`, and AT needs to
 *                                     be told either way), the native attribute
 *                                     additionally on the button, `onClick`
 *                                     withheld, and a muted surface in both.
 *                                     Deliberately NOT `pointer-events: none` —
 *                                     that also kills hover, and a disabled
 *                                     control the user cannot hover is one they
 *                                     cannot get a tooltip from either.
 *   onClick?: () => void              omit → renders a span; pass → real button, 44px target
 *   size?: 'sm' | 'md'                'sm' (default) reproduces SIT_CHIP_SX's
                                     BOX geometry: px 1.25, py 0.5, FS.sm,
                                     weight 600, lh 1.4, transparent border.
                                     🔴 It is NOT a full copy, and two of the
                                     differences bite on migration:
                                     • WRAPPING. `SIT_CHIP_SX` set
                                       `whiteSpace: 'nowrap'`; this pill does
                                       NOT by default — see the `nowrap` prop.
                                       Migrating that chip row without passing
                                       `nowrap` changes its wrap behaviour, and
                                       its labels are sentences. This line said
                                       "GEOMETRY exactly" until 2026-08-05, one
                                       commit after the nowrap default changed
                                       underneath it.
                                     • TONES. SitChip used hand-tuned rgba
                                       fills + MUI palette text, this uses the
                                       v2 canon.
                                     Swapping SitChip for Pill was therefore a
                                     visual change; it happened on 2026-08-11,
                                     with `nowrap` passed at every call site in
                                     that row so the wrap behaviour did NOT
                                     move with the colours.
 */
export default function Pill({
  children,
  tone = 'plain',
  suffix,
  tier,
  icon,
  count,
  selected,
  bordered = false,
  disabled = false,
  onClick,
  nowrap = false,
  size = 'sm',
  sx,
  ...rest
}) {
  const toneKey = TONES.includes(tone) ? tone : 'plain'
  const trailing = suffix ?? tier
  // A disabled pill is still a BUTTON when it has an onClick — demoting it to a
  // span on disable would make it appear and disappear from the tab order as
  // its state changes, and would strand focus if it were focused at the time.
  // It keeps the element and loses the behaviour.
  const interactive = typeof onClick === 'function'
  // `aria-pressed` follows SELECTED, not CLICKABILITY. A clickable pill that
  // never toggles (the recommendation chip opens the case detail) must not
  // announce a toggle state — a screen reader would report a control that
  // changes state, and it never does.
  const isToggle = interactive && selected !== undefined

  return (
    <Box
      component={interactive ? 'button' : 'span'}
      type={interactive ? 'button' : undefined}
      // The native attribute only where it is valid. On the span branch it
      // would be an invalid attribute that does nothing — which is exactly what
      // `...rest` used to produce.
      disabled={interactive ? disabled || undefined : undefined}
      // `aria-disabled` on BOTH branches: a static status chip can be
      // unavailable too, and a span has no other way to say so.
      aria-disabled={disabled || undefined}
      // No `disabled ? undefined : onClick` guard here, deliberately. A pill
      // with an `onClick` is ALWAYS the <button> branch, and a natively
      // disabled button never dispatches — so the guard would be unreachable
      // code, and the test asserting it could not be made to fail. It was
      // written, found un-failable by a mutation run, and removed rather than
      // left as a second mechanism nobody can verify.
      onClick={onClick}
      aria-pressed={isToggle ? !!selected : undefined}
      sx={(t) => {
        const v2 = v2Tone(t, toneKey)
        const plain = toneKey === 'plain'
        const wash = t.palette.mode === 'dark' ? V2.washDark : V2.wash
        return {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: size === 'md' ? 1.5 : 1.25,
          py: size === 'md' ? 0.75 : 0.5,
          borderRadius: 999,
          fontSize: size === 'md' ? FS.md : FS.sm,
          fontWeight: 600,
          lineHeight: 1.4,
          // Tabular figures on the ROOT, not only on `count`. The SLA chips
          // carry their digits IN THE LABEL ("SLA 24 h: 6 h übrig", "verletzt
          // seit 2 T") and the overview polls, so proportional figures make
          // exactly those numbers jump on every refresh.
          fontVariantNumeric: 'tabular-nums',
          // Opt-in, not default: a sentence-length situation label wraps.
          ...(nowrap && { whiteSpace: 'nowrap' }),
          // Calm fill, full tone only on text and border — mehrere gesättigte
          // Flächen nebeneinander sind laut Owner ein Fehler, kein Feature.
          bgcolor: plain ? wash : v2.soft,
          color: plain ? v2Tone(t, 'ink').ink70 : v2.main,
          // Reserved box by default; see the description before changing it.
          border: '1px solid',
          borderColor: bordered && !plain ? v2.c100 : 'transparent',
          // Owner-Entscheid 5 (2026-08-09): the selected state used to move ONLY
          // `borderColor`, and to a weak value — measured 1.31:1 neutral,
          // 1.12:1 brand. A state indicator nobody can see.
          //
          // The BORDER is the primary cue and now carries WCAG 1.4.11's 3:1
          // against the pill's own fill. MEASURED over paper, worst tone:
          //   plain  ink70 on washSelected   6.00 light · 4.81 dark
          //   toned  main  on soft           4.75 light · 4.59 dark
          // (`divider` was MUI stock rgba(0,0,0,.12) — nowhere near.)
          //
          // 🔴 The FILL steps for `plain` ONLY, and that is deliberate. Stepping
          // a TONED pill's fill to `c100` puts `main` on `c100`, which measures
          // 3.64–4.47 light and 3.02–4.05 dark — it would turn a state that
          // passes AA today (4.59–9.36) into one that fails, in six tones out of
          // six in dark mode. `c100` is the BORDER tone (theme.js:23-25); it is
          // not a surface that carries text. An earlier draft of this PR made
          // exactly that mistake AND set border and fill to the same value,
          // deleting the border it claimed to keep.
          //
          // So the owner's "zusätzlich über die Füllfläche" is honoured on the
          // neutral pills he measured (1.31:1) and NOT on toned ones, where it
          // is unbuildable without a new AA-safe fill token per tone. Flagged
          // back to him — see docs/OWNER-ENTSCHEIDE.md, Frage 5.
          ...(selected && {
            borderColor: plain ? v2Tone(t, 'ink').ink70 : v2.main,
            ...(plain && {
              bgcolor: t.palette.mode === 'dark' ? V2.washSelectedDark : V2.washSelected,
            }),
          }),
          // The disabled LOOK, which did not exist at all before.
          //
          // MEASURED, 2026-08-05 — text against its own fill at this opacity:
          //   dark  err 2.40 · agent 2.46      light  err 2.20 · agent 1.98
          // An earlier version of this comment asserted the value "stays above
          // the point where the text stops being legible". That was never
          // measured, and at 1.98:1 it is not self-evident, so the claim is
          // withdrawn and replaced by the numbers.
          //
          // It is NOT a WCAG failure: 1.4.3 exempts inactive controls
          // explicitly. And 0.45 is deliberately more generous than MUI's own
          // disabled opacity (0.38), which is the value the rest of this app
          // already ships. Read it as "consistent with the platform, and a
          // little kinder", not as a legibility guarantee.
          ...(disabled && { opacity: 0.45 }),
          // STRUCTURE — applies whenever the element is a <button>, disabled or
          // not. `fontFamily: 'inherit'` undoes the UA button TYPEFACE and the
          // 44px rule is the touch target; a disabled pill that lost either
          // would change SIZE and TYPEFACE on disable, i.e. reflow the row it
          // sits in. Only the affordances below are conditional.
          //
          // 🔴 `fontFamily`, NOT the `font` shorthand — measured 2026-08-11,
          // R15-B1, on this component's first interactive consumer. `font` is a
          // shorthand: emitted HERE it lands AFTER the `fontSize`/`fontWeight`/
          // `lineHeight`/`fontVariantNumeric` set at the top of this object and
          // resets all four to the inherited values. An interactive pill
          // therefore rendered at the app body font — MEASURED 14px/400 in
          // Chromium, tabular figures gone — beside static neighbours pinned at
          // 12.5px/600, i.e. the two chip languages in one row that this whole
          // component exists to end.
          //
          // ⚠️ This said "16px/400" for one commit, labelled "measured", and it
          // was not: `theme.js:142` sets `html { font-size: 14px }` and
          // `typeScale.test.js:64` pins that line. The defect was real either
          // way — the break is 12 %, not 28 % — but a number labelled measured
          // that nobody measured is the exact failure this file keeps warning
          // about, so the correction stays visible rather than being tidied.
          //
          // It survived review for six days because nothing consumed the
          // interactive branch. The one call site that predated `Pill`
          // (`SituationHeader`'s old `FollowUpChip`) wrote `font: 'inherit'` and
          // then re-applied `fontSize`/`fontWeight` on the NEXT two lines —
          // which is why nobody noticed the shorthand was doing the damage.
          // 🔴 That pattern is correct only for the longhands it re-applies.
          // The same construction still lives at `SituationHeader.jsx:836` (the
          // language toggle) and measurably resets `lineHeight` to CssBaseline's
          // 1.5 and drops `fontVariantNumeric` — harmless there today (two
          // letters, no digits) and one reordering away from being this bug
          // again. Filed rather than fixed: changing it moves a pixel on a
          // control this PR was not asked to touch.
          // jsdom does not implement the shorthand cascade, so no jest
          // test can see this through `getComputedStyle`; the guard in
          // `v2Bausteine.test.jsx` reads the EMITTED CSS instead.
          //
          // Only the family needs undoing: `fontSize`, `fontWeight`,
          // `lineHeight` and `fontVariantNumeric` are all set explicitly above
          // and already beat the UA button style.
          ...(interactive && {
            fontFamily: 'inherit',
            [t.breakpoints.down('sm')]: { minHeight: 44 },
            // Same focus treatment as StatTile — the pill overrides its own
            // border, so the UA default ring is not reliable here. Kept for the
            // disabled case too: a natively-disabled button is not focusable
            // anyway, so this only matters for a caller that disables via
            // `aria-disabled` on the span branch, where the ring is correct.
            '&:focus-visible': focusRing(),
          }),
          // AFFORDANCES — a pointer cursor and a hover response both promise
          // that clicking does something. Neither is true while disabled.
          ...(interactive && !disabled && {
            cursor: 'pointer',
            // K-5 (Welle 2): reine Wirkung (Filter) ohne Hintergrund.
            ...hoverOnly({ filter: 'brightness(0.97)' }),
          }),
          // Caller sx LAST, and merged rather than spread by JSX: `{...rest}`
          // used to carry it, where a later prop silently replaced the whole
          // style object — `<Pill sx={{ ml: 1 }}>` would have dropped the pill.
          ...(typeof sx === 'function' ? sx(t) : sx),
        }
      }}
      {...rest}
    >
      {icon}
      <Box component="span">{children}</Box>
      {/* No `fontVariantNumeric` here any more — it lives on the root, which
          the count inherits, and which the LABEL needs just as much. */}
      {Number.isFinite(count) && (
        <Box
          component="span"
          sx={(t) => {
            const plain = toneKey === 'plain'
            return {
              opacity: 0.7,
              // 🔴 R16-B1 (2026-11-03): this span used to INHERIT the root
              // colour, which for a plain pill is `ink70` — and the 0.7 here
              // multiplied it a second time to an effective **ink49**. Measured
              // from the token values: 3.15–3.69:1 at 12.5 px/600, i.e. below
              // WCAG AA (4.5:1), and below the repo's own rule at
              // tokens.js:1716-1717 ("load-bearing info … never below ink70").
              // A facet count is load-bearing — it is what the chip is for.
              // FULL ink under the same 0.7 lands at 4.65–6.36:1 and keeps the
              // count visibly subordinate to the label. Toned pills keep the
              // tone's `main`, exactly what they inherited before — unchanged.
              color: plain
                ? (t.palette.mode === 'dark' ? V2.inkDark : V2.ink)
                : v2Tone(t, toneKey).main,
            }
          }}
        >
          {count}
        </Box>
      )}
      {trailing && (
        // The canon's in-pill pattern: a text suffix, never a filled badge.
        // The filled variant was removed on 2026-07-31; TierBadge is the
        // standalone case and must stay separate.
        <Box
          component="span"
          sx={{ fontSize: FS.xxs, fontWeight: 700, opacity: 0.65, ml: 0.25 }}
        >
          {trailing}
        </Box>
      )}
    </Box>
  )
}
